import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';

// components
import NoData from 'components/noData/noData';
import ListButton from 'components/listButton/listButton';
import ActionListHeaderComponent from '../../listHeader';
import StatusComponent from 'components/status/status';

// services

// styles

// models
import { Notice } from '../models/notice';
import { onGetNoticesList } from '../services/noticeService';
import NotificationService from 'core/services/notification';
import TableTotal from 'components/tableTotal/tableTotal';
import { ServiceCost } from '../../commonModels';
import { onGetServiceCost } from '../../commonServices';

interface NoticeListPageProps {}

const NoticeListPage: FunctionComponent<NoticeListPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [requests, setRequests] = useState<Notice[]>([]);

	const [serviceCostLoading, setServiceCostLoading] = useState<boolean>(true);
	const [serviceCost, setServiceCost] = useState<ServiceCost>();

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const columns: TableProps<Notice>['columns'] = [
		{
			title: t('ACTIONS.REQUEST_CODE'),
			dataIndex: 'request_number',
			key: 'code',
		},
		{
			title: t('ACTIONS.REQUEST_DATE'),
			dataIndex: 'created_at',
			key: 'date',
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status) => <StatusComponent text={t(`COMMON.${status.toUpperCase()}`)} color={'green'} />,
		},
		{
			title: t('ACTIONS.SHAREHOLDER_NAME'),
			dataIndex: 'shareholder_name',
			key: 'shareholder_name',
			render: (_, record) => record.notifiable.name,
		},
		{
			title: t('ACTIONS.BY'),
			dataIndex: 'by',
			key: 'by',
			render: (_, record) => record.company_user ? record.company_user.name : '--',
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: 'actions',
			key: 'actions',
			render: (_, record) => <ListButton type="view" onClick={() => navigate(`${record.id}`)} />,
		},
	];

	useEffect(() => {
		setCurrentPage(getCurrentPage());
		getNoticesList();
		getServiceCost();
	}, []);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const getServiceCost = async () => {
		try {
			setServiceCostLoading(true);

			const response = await onGetServiceCost('specific_notice');
			setServiceCost(response?.success);

			setServiceCostLoading(false);
		} catch (error) {
			setServiceCostLoading(false);
		}
	};

	const getNoticesList = async () => {
		try {
			setIsLoading(true);

			const response = await onGetNoticesList(getCurrentPage());

			setRequests(response.notice_actions);
			setTotalRecords(response.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setIsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetNoticesList(getCurrentPage());
			setRequests(response.notice_actions);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	return (
		<>
			<ActionListHeaderComponent
				title={t('ACTIONS.NOTICE')}
				subTitle={t('ACTIONS.SEND_OFFICIAL_NOTICE')}
				costLoading={serviceCostLoading}
				costs={[serviceCost!]}
			/>

			<Table
				columns={columns}
				dataSource={requests}
				scroll={{ x: 600 }}
				rowKey="id"
				loading={isLoading}
				pagination={{
					pageSize: 15,
					total: totalRecords,
					showSizeChanger: false,
					current: currentPage,
					onChange: fetchMoreData,
					showTotal: (total) => <TableTotal total={total} />,
				}}
				locale={{ emptyText: <NoData /> }}
			/>
		</>
	);
};

export default NoticeListPage;
